import { ApiErrorSchema } from '@estimating/contracts';
import type { z } from 'zod';

const REQUEST_TIMEOUT_MS = 5_000;
const REQUEST_ID_HEADER = 'x-request-id';

/**
 * A request that failed. `code` is the API's own, such as `auth.token_revoked`, when it
 * sent one (D-010); otherwise ours: `network.unreachable`, `response.invalid`,
 * `auth.token_unavailable` or `http.<status>`. The UI shows it and translates around it
 * (D-066).
 */
export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    /** The HTTP status, or null when no response came back. */
    readonly status: number | null = null,
    readonly requestId: string | null = null,
    options?: ErrorOptions,
  ) {
    super(code, options);
    this.name = 'ApiRequestError';
  }
}

interface ApiResponse<T> {
  readonly data: T;
  readonly latencyMs: number;
  readonly requestId: string | null;
}

interface RequestOptions {
  /** Cancels the request, for example when TanStack Query discards it. */
  readonly signal?: AbortSignal;
}

export interface ApiClient {
  get<T>(path: string, schema: z.ZodType<T>, options?: RequestOptions): Promise<ApiResponse<T>>;
}

/**
 * The signed-in user's Firebase ID token, or null when nobody is signed in. With
 * `forceRefresh`, a new one even if the current one looks valid.
 */
export type IdTokenSource = (options: { readonly forceRefresh: boolean }) => Promise<string | null>;

export interface ApiClientDeps {
  /** Sent with every request, so the API knows who is asking (D-059). */
  readonly idToken: IdTokenSource;
  readonly fetch?: typeof fetch;
  readonly now?: () => number;
  readonly timeoutMs?: number;
}

/** The `code` of an `ApiError` body, if the response has one. */
async function apiErrorCodeOf(response: Response): Promise<string | undefined> {
  try {
    const parsed = ApiErrorSchema.safeParse(await response.json());
    return parsed.success ? parsed.data.code : undefined;
  } catch {
    return undefined;
  }
}

export function createApiClient(
  baseUrl: string,
  {
    idToken,
    fetch: fetchImpl = (input, init) => fetch(input, init),
    now = () => performance.now(),
    timeoutMs = REQUEST_TIMEOUT_MS,
  }: ApiClientDeps,
): ApiClient {
  return {
    async get(path, schema, options = {}) {
      const signals = [AbortSignal.timeout(timeoutMs)];
      if (options.signal) {
        signals.push(options.signal);
      }
      const signal = AbortSignal.any(signals);
      const url = new URL(path, baseUrl);

      /** One attempt, with the user's token when someone is signed in. */
      async function send(forceRefresh: boolean) {
        let token: string | null;
        try {
          token = await idToken({ forceRefresh });
        } catch (error) {
          throw new ApiRequestError('auth.token_unavailable', null, null, { cause: error });
        }
        const headers: Record<string, string> = { accept: 'application/json' };
        if (token !== null) {
          headers.authorization = `Bearer ${token}`;
        }
        try {
          return { response: await fetchImpl(url, { headers, signal }), sentToken: token !== null };
        } catch (error) {
          // A caller's own cancellation isn't a failure to report.
          if (options.signal?.aborted) {
            throw error;
          }
          throw new ApiRequestError('network.unreachable', null, null, { cause: error });
        }
      }

      const startedAt = now();
      const first = await send(false);
      // The token may have expired on the way, or been revoked. A fresh one fixes the
      // first; for the second, Firebase can't renew it and signs the user out.
      const response =
        first.response.status === 401 && first.sentToken
          ? (await send(true)).response
          : first.response;

      const requestId = response.headers.get(REQUEST_ID_HEADER);
      if (!response.ok) {
        const code = (await apiErrorCodeOf(response)) ?? `http.${response.status}`;
        throw new ApiRequestError(code, response.status, requestId);
      }

      let body: unknown;
      try {
        body = await response.json();
      } catch (error) {
        throw new ApiRequestError('response.invalid', response.status, requestId, {
          cause: error,
        });
      }
      const latencyMs = now() - startedAt;

      const parsed = schema.safeParse(body);
      if (!parsed.success) {
        throw new ApiRequestError('response.invalid', response.status, requestId, {
          cause: parsed.error,
        });
      }
      return { data: parsed.data, latencyMs, requestId };
    },
  };
}
